import React, { useEffect, useState } from "react";
import AddVaccineModal from "./AddVaccine";
import { Vaccine, VaccineFormData } from "../../interfaces/vaccines";
import {
  deleteVaccine,
  getVaccineById,
  getVaccines,
} from "../../apis/vaccines";
import MastersBreadcrumb from "../../components/MastersBreadcrumb";

const unwrapList = (res: any): Vaccine[] => {
  const data = res?.data ?? res;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
};

const VaccinesList: React.FC = () => {
  const [vaccines, setVaccines] = useState<Vaccine[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [editableId, setEditableId] = useState<number | null>(null);
  const [selectedVaccine, setSelectedVaccine] =
    useState<VaccineFormData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchVaccines();
  }, []);

  const fetchVaccines = async () => {
    setLoading(true);
    try {
      const res: any = await getVaccines({ per_page: 1000 });
      setVaccines(unwrapList(res));
    } catch (error) {
      console.log("Error fetching vaccines:", error);
    } finally {
      setLoading(false);
    }
  };

  const openForCreate = () => {
    setEditableId(null);
    setSelectedVaccine(null);
    setOpenModal(true);
  };

  const openForEdit = async (id: number) => {
    try {
      const res: any = await getVaccineById(id);
      const data = res?.data ?? res;
      setEditableId(id);
      setSelectedVaccine({
        name: data?.name ?? "",
        shortName: data?.shortName ?? data?.short_name ?? "",
        description: data?.description ?? "",
        manufacturer: data?.manufacturer ?? "",
        vaccineType: data?.vaccineType ?? data?.vaccine_type ?? "",
        route: data?.route ?? "",
        site: data?.site ?? "",
        isActive: data?.isActive ?? data?.is_active ?? true,
        schedules: (data?.schedules || []).map((s: any) => ({
          id: s.id,
          doseNumber: s.doseNumber ?? s.dose_number ?? 1,
          recommendedAgeDays:
            s.recommendedAgeDays ?? s.recommended_age_days ?? null,
          recommendedAgeMonths:
            s.recommendedAgeMonths ?? s.recommended_age_months ?? null,
          recommendedAgeYears:
            s.recommendedAgeYears ?? s.recommended_age_years ?? null,
          minimumAgeDays: s.minimumAgeDays ?? s.minimum_age_days ?? null,
          minimumIntervalDays:
            s.minimumIntervalDays ?? s.minimum_interval_days ?? null,
          isBooster: s.isBooster ?? s.is_booster ?? false,
          notes: s.notes ?? "",
        })),
      });
      setOpenModal(true);
    } catch (error) {
      console.log("Error fetching vaccine:", error);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this vaccine and its schedules?")) return;
    try {
      await deleteVaccine(id);
      fetchVaccines();
    } catch (error: any) {
      alert(
        error?.response?.data?.message ||
          "Could not delete vaccine. It may have immunization records."
      );
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <MastersBreadcrumb current="Vaccines" />
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Vaccines
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage vaccine catalog and dose schedules.
          </p>
        </div>
        <button
          type="button"
          onClick={openForCreate}
          className="px-3 py-2 text-sm font-semibold text-white rounded-md shadow-sm bg-primary hover:bg-hover"
        >
          Add Vaccine
        </button>
      </div>

      <AddVaccineModal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setEditableId(null);
          setSelectedVaccine(null);
        }}
        onSuccess={fetchVaccines}
        editableId={editableId}
        vaccineData={selectedVaccine}
      />

      <div className="mt-8 flow-root">
        <div className="overflow-x-auto -mx-4 ring-1 ring-gray-300 sm:mx-0 sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th className="py-3.5 pr-3 pl-4 text-sm font-semibold text-left text-gray-900 sm:pl-6">
                  Name
                </th>
                <th className="px-3 py-3.5 text-sm font-semibold text-left text-gray-900">
                  Short Name
                </th>
                <th className="px-3 py-3.5 text-sm font-semibold text-left text-gray-900">
                  Type
                </th>
                <th className="px-3 py-3.5 text-sm font-semibold text-left text-gray-900">
                  Doses
                </th>
                <th className="px-3 py-3.5 text-sm font-semibold text-left text-gray-900">
                  Status
                </th>
                <th className="relative py-3.5 pr-4 pl-3 sm:pr-6">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-sm text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : vaccines.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-sm text-center text-gray-500">
                    No vaccines found. Add one to get started.
                  </td>
                </tr>
              ) : (
                vaccines.map((v) => (
                  <tr key={v.id}>
                    <td className="py-4 pr-3 pl-4 text-sm font-medium text-gray-900 sm:pl-6">
                      {v.name}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      {v.shortName || "-"}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      {v.vaccineType || "-"}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      {v.schedules?.length ?? 0}
                    </td>
                    <td className="px-3 py-4 text-sm">
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${
                          v.isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {v.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="py-4 pr-4 pl-3 text-sm font-medium text-right sm:pr-6">
                      <button
                        type="button"
                        onClick={() => openForEdit(v.id)}
                        className="mr-3 text-indigo-600 hover:text-indigo-900"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(v.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default VaccinesList;
